/**
 * Étapes 3, 4, 5 et 6 du formulaire de garantie de loyer
 * Séparé pour respecter la limite de ~300 lignes par fichier
 */
import React from 'react';
import { Home, Building2, Users, CheckCircle } from 'lucide-react';

// Champ de formulaire réutilisable avec React.memo pour éviter re-renders
const FormField = React.memo(({ label, type = 'text', value, onChange, required = false, placeholder = '', options = null, error = null }) => (
  <div className="mb-4">
    <label className="block text-sm text-gray-300 mb-2">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {options ? (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white focus:outline-none ${error ? 'border-red-400 focus:border-red-400' : 'border-white/20 focus:border-blue-400'}`}
        required={required}
      >
        <option value="" className="bg-gray-800">Sélectionner...</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value} className="bg-gray-800">{opt.label}</option>
        ))}
      </select>
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white placeholder-gray-500 focus:outline-none ${error ? 'border-red-400 focus:border-red-400' : 'border-white/20 focus:border-blue-400'}`}
      />
    )}
    {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
  </div>
));

// Étape 3: Objet loué
export const StepObjetLoue = ({ formData, updateField, validationErrors = {} }) => (
  <div>
    <div className="flex items-center gap-3 mb-6">
      <Home className="text-blue-400" size={28} />
      <h3 className="text-2xl font-bold text-white">Objet loué</h3>
    </div>
    
    <FormField
      label="Adresse du bien"
      value={formData.objetLoue.adresse}
      onChange={(v) => updateField('objetLoue', 'adresse', v)}
      required
      placeholder="Rue et numéro"
      error={validationErrors.adresseObjet}
    />
    
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <FormField
        label="NPA"
        value={formData.objetLoue.npa}
        onChange={(v) => updateField('objetLoue', 'npa', v)}
        required
        error={validationErrors.npaObjet}
      />
      <FormField
        label="Ville"
        value={formData.objetLoue.ville}
        onChange={(v) => updateField('objetLoue', 'ville', v)}
        required
        error={validationErrors.villeObjet}
      />
    </div>
    
    <FormField
      label="Référence du bien (optionnel)"
      value={formData.objetLoue.reference}
      onChange={(v) => updateField('objetLoue', 'reference', v)}
      placeholder="N° d'appartement, étage, etc."
    />
    
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <FormField
        label="Date de début du bail"
        type="date"
        value={formData.objetLoue.dateDebutBail}
        onChange={(v) => updateField('objetLoue', 'dateDebutBail', v)}
        required
        error={validationErrors.dateDebutBail}
      />
      <FormField
        label="Date de début de la police"
        type="date"
        value={formData.objetLoue.dateDebutPolice}
        onChange={(v) => updateField('objetLoue', 'dateDebutPolice', v)}
        required
        error={validationErrors.dateDebutPolice}
      />
    </div>
  </div>
);

// Étape 4: Bailleur
export const StepBailleur = ({ formData, updateField, validationErrors = {} }) => (
  <div>
    <div className="flex items-center gap-3 mb-6">
      <Building2 className="text-blue-400" size={28} />
      <h3 className="text-2xl font-bold text-white">Informations du bailleur</h3>
    </div>
    
    <FormField
      label="Type de bailleur"
      value={formData.bailleur.type}
      onChange={(v) => updateField('bailleur', 'type', v)}
      required
      options={[
        { value: 'particulier', label: 'Particulier' },
        { value: 'regie', label: 'Régie / Société immobilière' }
      ]}
      error={validationErrors.typeBailleur}
    />
    
    {formData.bailleur.type === 'regie' && (
      <FormField
        label="Nom de la société / Régie"
        value={formData.bailleur.nomSociete}
        onChange={(v) => updateField('bailleur', 'nomSociete', v)}
        required
        error={validationErrors.nomSociete}
      />
    )}
    
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <FormField
        label="Prénom du contact"
        value={formData.bailleur.prenom}
        onChange={(v) => updateField('bailleur', 'prenom', v)}
        required
        error={validationErrors.prenomBailleur}
      />
      <FormField
        label="Nom du contact"
        value={formData.bailleur.nom}
        onChange={(v) => updateField('bailleur', 'nom', v)}
        required
        error={validationErrors.nomBailleur}
      />
    </div>
    
    <FormField
      label="Adresse"
      value={formData.bailleur.adresse}
      onChange={(v) => updateField('bailleur', 'adresse', v)}
      required
      error={validationErrors.adresseBailleur}
    />
    
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <FormField
        label="NPA"
        value={formData.bailleur.npa}
        onChange={(v) => updateField('bailleur', 'npa', v)}
        required
        error={validationErrors.npaBailleur}
      />
      <FormField
        label="Ville"
        value={formData.bailleur.ville}
        onChange={(v) => updateField('bailleur', 'ville', v)}
        required
        error={validationErrors.villeBailleur}
      />
    </div>
    
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <FormField
        label="E-mail"
        type="email"
        value={formData.bailleur.email}
        onChange={(v) => updateField('bailleur', 'email', v)}
        required
        error={validationErrors.emailBailleur}
      />
      <FormField
        label="Téléphone"
        type="tel"
        value={formData.bailleur.telephone}
        onChange={(v) => updateField('bailleur', 'telephone', v)}
        required
        placeholder="Ex: 079 123 45 67"
        error={validationErrors.telephoneBailleur}
      />
    </div>
  </div>
);

// Étape 5: Co-locataire + Consentements
export const StepColocataireConsent = ({ formData, updateField, validationErrors = {} }) => (
  <div>
    <div className="flex items-center gap-3 mb-6">
      <Users className="text-blue-400" size={28} />
      <h3 className="text-2xl font-bold text-white">Co-locataire & Consentements</h3>
    </div>
    
    {/* Co-locataire */}
    <div className="mb-6 p-4 bg-white/5 rounded-lg">
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.colocataire.hasColocataire}
          onChange={(e) => updateField('colocataire', 'hasColocataire', e.target.checked)}
          className="w-5 h-5 rounded"
        />
        <span className="text-white">Ajouter un co-locataire</span>
      </label>
    </div>
    
    {formData.colocataire.hasColocataire && (
      <div className="mb-6 p-4 border border-white/10 rounded-lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            label="Prénom"
            value={formData.colocataire.prenom}
            onChange={(v) => updateField('colocataire', 'prenom', v)}
            required
            error={validationErrors.prenomColoc}
          />
          <FormField
            label="Nom"
            value={formData.colocataire.nom}
            onChange={(v) => updateField('colocataire', 'nom', v)}
            required
            error={validationErrors.nomColoc}
          />
        </div>
        <FormField
          label="Date de naissance"
          type="date"
          value={formData.colocataire.dateNaissance}
          onChange={(v) => updateField('colocataire', 'dateNaissance', v)}
          required
          error={validationErrors.dateNaissanceColoc}
        />
      </div>
    )}
    
    {/* Consentements */}
    <div className="space-y-4 mt-8">
      <h4 className="text-lg font-semibold text-white mb-4">Consentements</h4>
      
      <div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.accepteConditions}
            onChange={(e) => updateField(null, 'accepteConditions', e.target.checked)}
            className="w-5 h-5 rounded mt-0.5"
            required
          />
          <span className="text-sm text-gray-300">
            J'accepte les <a href="/mentions-legales" target="_blank" className="text-blue-400 hover:underline">conditions générales</a> et 
            confirme que les informations fournies sont exactes. <span className="text-red-400">*</span>
          </span>
        </label>
        {validationErrors.accepteConditions && <p className="text-red-400 text-xs mt-1 ml-8">{validationErrors.accepteConditions}</p>}
      </div>
      
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.accepteTraitement}
          onChange={(e) => updateField(null, 'accepteTraitement', e.target.checked)}
          className="w-5 h-5 rounded mt-0.5"
        />
        <span className="text-sm text-gray-300">
          J'accepte de recevoir des informations commerciales de la part d'ATOM Group SA.
        </span>
      </label>
    </div>
  </div>
);

// Étape 6: Confirmation
export const StepConfirmation = ({ onClose }) => (
  <div className="text-center py-8">
    <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
      <CheckCircle className="text-green-400" size={48} />
    </div>
    
    <h3 className="text-2xl font-bold text-white mb-4">Demande envoyée avec succès !</h3>
    
    <p className="text-gray-300 mb-6 max-w-md mx-auto">
      Nous avons bien reçu votre demande de garantie de loyer. 
      Un conseiller ATOM Group vous contactera dans les plus brefs délais.
    </p>
    
    <button
      onClick={onClose}
      className="btn-glass btn-primary px-8 py-3"
    >
      Fermer
    </button>
  </div>
);

export default { StepObjetLoue, StepBailleur, StepColocataireConsent, StepConfirmation };
